import { TableContainer, Paper, Table, TableHead, TableRow, TableCell, TableBody, Pagination, Box, Typography, Link } from "@mui/material";
import { Props, defaultColumnDef, typedColumnDefs } from "./models";
import { useEffect, useMemo, useState } from "react";
import { editCells, getRowEditState } from "./api";

export function DataGrid<T = any>({
  rows,
  columns,
  columnDefaults,
  sx,
  rowHeight = "40px",
  getRowStyle,
  isRowEditable,
  onRowUpdate,
  state: rowEditState,
  onStateChange: onRowEditStateChange,
  pageSize,
  rowsPerPage,
  onRowClick
}: Props<T>) {
  const [internalState, setInternalState] = useState<any>({});
  const [page, setPage] = useState(0);
  const [pageSizeState, setPageSizeState] = useState<number | undefined>(pageSize || (rowsPerPage && rowsPerPage[0]));
  const columnDefs = useMemo(() => (columns ?? []).map(c => ({ ...(c.type ? typedColumnDefs[c.type] : defaultColumnDef), ...columnDefaults, ...c })), [columns]);
  const currentState: any = rowEditState ?? internalState;

  useEffect(() => setInternalState({}), [rows, columns]);

  const viewRows = useMemo(() => {
    if (pageSizeState) return rows.slice(page * pageSizeState, (page + 1) * pageSizeState);
    return rows;
  }, [rows, page, pageSizeState]);

  return (
    <Box sx={{
      display: "flex",
      flexDirection: "column",
      height: "100%"
    }}>
      <TableContainer component={Paper} sx={{
        ...sx,
        overflow: 'auto',
        maxHeight: '100%',
        flex: 1,
        display: 'flex',
        flexDirection: 'column'
      }}>
        <Table>
          <TableHead>
            <TableRow sx={{ height: rowHeight }}>
              {columnDefs.map((col, i) =>
                <TableCell key={i} sx={{ width: col.width, paddingRight: "10px", textAlign: col.align || "left" }}>
                  {typeof col.headerName === "string"
                    ? col.headerName
                    : col.headerName}
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {viewRows.map((row: any, i) => {
              const rowEditable = isRowEditable?.({ row });
              const rowEditState = rowEditable ? getRowEditState(currentState, row.id) : undefined;

              return (<TableRow key={row.id ?? i} sx={{ height: rowHeight, ...getRowStyle?.({ row }), ...(onRowClick && { cursor: 'pointer' }) }} onClick={() => onRowClick?.(row)}>
                {columnDefs.map(column => {
                  const params = { row, column };

                  let cell = column.field ? row[column.field] : undefined;
                  if (!cell)
                    cell = column.alternative ? row[column.alternative] : undefined;
                  const cellEditable = rowEditable && column.editable;
                  let handleCellClick: any = undefined;

                  if (cellEditable && column.renderEditor && rowEditState?.[column.field as string]) {
                    cell = column.renderEditor({
                      ...params, value: cell, setValue: val => {
                        row[column.field as string] = val;
                        onRowUpdate?.({ row });
                      }
                    });
                  } else {
                    if (column.formatter)
                      cell = column.formatter({ value: cell, ...params });

                    if (column.renderer)
                      cell = column.renderer({ value: cell, ...params });

                    if (cellEditable)
                      handleCellClick = () => {
                        const newState = editCells(currentState, row.id, [column.field as string]);
                        setInternalState(newState);
                        onRowEditStateChange?.(newState);
                      }
                  }

                  return (<TableCell
                    key={column.field}
                    sx={{ width: column.width, textAlign: column.align || "left" }}
                    onClick={handleCellClick}
                  >
                    {cell}
                  </TableCell>);
                })}
              </TableRow>);
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <Box sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        marginTop: "8px"
      }}>
        {pageSizeState && (<Pagination sx={{ marginTop: 1 }} count={Math.ceil(rows.length / pageSizeState)} onChange={(e, v) => setPage(v - 1)} />)}
        {rowsPerPage && <Box sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "8px"
        }}>
          <Typography variant="body2">Rows per page: </Typography>
          {rowsPerPage.map((r, i) => <Link href="#" key={i} underline={r === pageSizeState ? "always" : "none"} onClick={() => setPageSizeState(r)}>{r}</Link>)}
        </Box>}
      </Box>
    </Box>
  );
}